// =============================================================================
//  SCREEN: CertificateScreen (mit präziser PDF-Koordinaten-Umrechnung)
// =============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../api';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import { CLASSIFICATIONS } from '../constants/classificationConstants';

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous"; // Wichtig für das Laden von Bildern
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
  });
};

const CertificateScreen = () => {
  const { id: tournamentId } = useParams();
  const [tournament, setTournament] = useState(null);
  const [scores, setScores] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loading, setLoading] = useState(true);
  
  const [certificates, setCertificates] = useState([]);
  const [filter, setFilter] = useState('all');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tournamentRes, scoresRes, templatesRes] = await Promise.all([
          apiClient.get(`/api/tournaments/${tournamentId}`),
          apiClient.get(`/api/tournaments/${tournamentId}/scores`),
          apiClient.get('/api/templates'),
        ]);
        setTournament(tournamentRes.data);
        setScores(scoresRes.data);
        setTemplates(templatesRes.data);
        if (templatesRes.data.length > 0) {
          setSelectedTemplateId(templatesRes.data[0]._id);
        }
      } catch (err) {
        toast.error('Fehler beim Laden der Daten.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tournamentId]);

  const getClassificationLabel = (key) => CLASSIFICATIONS.find(c => c.key === key)?.label || key;

  const rankedResults = useMemo(() => {
    if (!scores.length) return {};
    const validScores = scores.filter(score => score.entry && score.entry.player);
    const grouped = validScores.reduce((acc, score) => {
      const key = score.entry?.classification;
      if (key) {
        if (!acc[key]) acc[key] = [];
        acc[key].push(score);
      }
      return acc;
    }, {});
    for (const key in grouped) {
      grouped[key].sort((a, b) => {
        if (b.totalGesamt !== a.totalGesamt) return b.totalGesamt - a.totalGesamt;
        if (b.totalAbr !== a.totalAbr) return b.totalAbr - a.totalAbr;
        return a.totalFw - b.totalFw;
      });
    }
    return grouped;
  }, [scores]);
  
  const generatePreview = () => {
    if (!tournament) return;
    const certs = [];
    Object.entries(rankedResults).forEach(([key, results]) => {
      results.slice(0, 3).forEach((score, index) => {
        if (!score.entry || !score.entry.player) return;
        const rank = index + 1;
        const [filterType, filterRank, filterKey] = filter.split(':');
        if (filterType === 'classification' && filterKey !== key) return;
        if (filterType === 'single' && (parseInt(filterRank) !== rank || filterKey !== key)) return;
        
        certs.push({
          id: `${key}-${rank}`,
          '[Anrede]': key.includes('damen') ? 'Frau' : 'Herr',
          '[Vorname]': score.entry.player.firstName,
          '[Nachname]': score.entry.player.lastName,
          '[Name (komplett)]': `${score.entry.player.firstName} ${score.entry.player.lastName}`,
          '[Mannschaft]': score.entry.team?.name || score.entry.player.club?.name || score.entry.player.guestClubName || '',
          '[Gesamtholz]': score.totalGesamt,
          '[Platz]': rank,
          '[Wertung]': getClassificationLabel(key),
          '[Datum]': new Date().toLocaleDateString('de-DE'),
          '[Turniername]': tournament.name,
          '[Ort]': tournament.location || 'Mürzzuschlag',
        });
      });
    });
    setCertificates(certs);
    toast.info(`${certs.length} Urkunden für die Vorschau generiert.`);
  };

  const handleCertChange = (index, field, value) => {
    const newCerts = [...certificates];
    newCerts[index][field] = value;
    setCertificates(newCerts);
  };
  
  const generateFinalPDF = async () => {
    if (!selectedTemplateId) return toast.error("Bitte zuerst eine Vorlage auswählen.");
    if (!certificates.length) return toast.warn("Bitte zuerst eine Vorschau generieren.");
    
    setIsGeneratingPdf(true);
    toast.info("Erstelle PDF... dies kann einen Moment dauern.");

    const selectedTemplate = templates.find(t => t._id === selectedTemplateId);
    if (!selectedTemplate) {
        setIsGeneratingPdf(false);
        return toast.error("Ausgewählte Vorlage nicht gefunden.");
    }

    try {
      const imagesToLoad = {};
      if (selectedTemplate.backgroundImage) {
        imagesToLoad.background = loadImage(selectedTemplate.backgroundImage);
      }
      selectedTemplate.elements.forEach(el => {
        if (el.type === 'image') {
          imagesToLoad[el.id] = loadImage(el.content);
        }
      });
      const loadedImages = await Promise.all(Object.values(imagesToLoad));
      const imageMap = new Map(Object.keys(imagesToLoad).map((key, i) => [key, loadedImages[i]]));
      
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      
      const A4_WIDTH_MM = 210;
      const A4_HEIGHT_MM = 297;
      const CANVAS_WIDTH_PX = 794;
      const CANVAS_HEIGHT_PX = CANVAS_WIDTH_PX * (A4_HEIGHT_MM / A4_WIDTH_MM);

      for (const [index, data] of certificates.entries()) {
        if (index > 0) doc.addPage();
        
        if (imageMap.has('background')) {
            doc.addImage(imageMap.get('background'), 'PNG', 0, 0, A4_WIDTH_MM, A4_HEIGHT_MM, undefined, 'FAST');
        }

        for (const el of selectedTemplate.elements) {
          if (!el.printable) continue;

          const x_mm = (el.x / CANVAS_WIDTH_PX) * A4_WIDTH_MM;
          const y_mm = (el.y / CANVAS_HEIGHT_PX) * A4_HEIGHT_MM;
          const width_mm = (el.width / CANVAS_WIDTH_PX) * A4_WIDTH_MM;
          const height_mm = (el.height / CANVAS_HEIGHT_PX) * A4_HEIGHT_MM;

          if (el.type === 'image' && imageMap.has(el.id)) {
            doc.addImage(imageMap.get(el.id), 'PNG', x_mm, y_mm, width_mm, height_mm, undefined, 'FAST');
          } else if (el.type === 'line') {
              doc.setDrawColor(el.color);
              doc.setLineWidth(height_mm);
              doc.line(x_mm, y_mm, x_mm + width_mm, y_mm);
          } else if (el.type === 'text' || el.type === 'placeholder') {
              let fontStyle = 'normal';
              if (el.fontWeight === 'bold') fontStyle = 'bold';
              if (el.fontWeight === 'italic') fontStyle = 'italic';

              doc.setFont(el.fontFamily, fontStyle);
              
              const fontSizePt = parseInt(el.fontSize) * (A4_WIDTH_MM / CANVAS_WIDTH_PX) * (72 / 25.4);
              doc.setFontSize(fontSizePt);
              doc.setTextColor(el.color);
              
              let content = el.type === 'placeholder' ? (data[el.content] || '') : el.content;
              
              if (el.content === '[Platz]') {
                content = `${content}. Platz`;
              }
              if (el.content === '[Gesamtholz]') {
                content = `${content} Holz`;
              }

              const textLines = doc.splitTextToSize(content.toString(), width_mm);
              doc.text(textLines, x_mm, y_mm, { align: el.textAlign, baseline: 'top' });
          }
        }
      }

      doc.save(`Urkunden_${tournament.name.replace(/\s/g, '_')}.pdf`);
    } catch (error) {
      toast.error("Fehler beim Erstellen des PDF. Ein Bild konnte möglicherweise nicht geladen werden.");
      console.error(error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (loading) return <p>Lade Daten...</p>;

  return (
    <div className="screen-container">
      <h2>Urkunden für {tournament?.name}</h2>
      <div className="certificate-controls">
        <div className="control-group">
          <h4>1. Vorlage auswählen</h4>
          <select value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)}>
            <option value="" disabled>Bitte wählen...</option>
            {templates.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
          </select>
        </div>
        <div className="control-group">
          <h4>2. Personen auswählen</h4>
          <select value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">Alle Urkunden (Platz 1-3)</option>
            <optgroup label="Pro Wertung">
              {Object.keys(rankedResults).map(key => (
                <option key={key} value={`classification:${key}`}>{getClassificationLabel(key)}</option>
              ))}
            </optgroup>
            <optgroup label="Einzelne Urkunde">
              {Object.entries(rankedResults).flatMap(([key, results]) => 
                results.slice(0, 3).map((_, index) => (
                  <option key={`${key}-${index+1}`} value={`single:${index+1}:${key}`}>
                    Platz {index+1} - {getClassificationLabel(key)}
                  </option>
                ))
              )}
            </optgroup>
          </select>
        </div>
        <div className="control-group">
          <h4>3. Aktionen</h4>
          <button onClick={generatePreview} className="btn-secondary-action">Vorschau generieren</button>
          <button onClick={generateFinalPDF} className="btn-primary-action" disabled={isGeneratingPdf}>
            {isGeneratingPdf ? 'Erstelle...' : 'PDFs generieren'}
          </button>
        </div>
      </div>
      
      <hr />
      
      <h3>Vorschau (bearbeitbar)</h3>
      <div className="certificates-preview-grid">
        {certificates.map((cert, index) => (
          <div key={cert.id} className="certificate-preview">
            {Object.entries(cert).map(([key, value]) => {
                if (key === 'id') return null;
                return (
                    <div className="form-group" key={key}>
                        <label>{key.replace(/\[|\]/g, '')}</label>
                        <input 
                            value={value} 
                            onChange={e => handleCertChange(index, key, e.target.value)} 
                        />
                    </div>
                )
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CertificateScreen;